import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerTransactionHistoryInterFace,
  RequestConsumerTransactionHistoryInterFace,
} from '../../../../interface/end-consumer-transaction-history-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchConsumerTransactionHistory,
  fetchConsumerTransactionHistoryFilter,
  setSortData,
  updateConsumerTransactionHistoryListFilter,
} from '../../../../redux/slices/EndConsumerTransactionHistory';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import EmspCdrSidePanel from '../../../../shared-components/table-side-panel/EmspCdrSidePanel';
import EmspSessionSidePanel from '../../../../shared-components/table-side-panel/EmspSessionSidePanel';
import EndConsumerSidePanel from '../../../../shared-components/table-side-panel/EndConsumerSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { convertUtcTo12HourTime, getDaysBetween, removeEmptyKeys } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

/**
 * Charge Session List
 * @returns {React.JSX.Element} The TransactionHistoryList component
 */
function TransactionHistoryList(): React.JSX.Element {
  const { t, i18n } = useTranslation(['transaction', 'endConsumer']);
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerTransactionHistory);
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);

  const currentDate = new Date().toISOString().split('T')[0];

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: currentDate,
    endDate: currentDate,
  });

  /**
   * Clear date range.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render date filters.
   * @returns {React.JSX.Element} The rendered date filters.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetch Users list function
   * @param {RequestConsumerTransactionHistoryInterFace} data - The data
   */
  const fetchData = (data: RequestConsumerTransactionHistoryInterFace) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerTransactionHistory({
          userCustomId: [selectedEndConsumer.userCustomId],
          ...data,
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ 
      ...queryParameters, 
      page, 
      limit, 
      sortField, 
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const [searchParameters] = useSearchParams();
  const location = useLocation();

  const hasProfile =
    searchParameters.has('profile') ||
    Boolean(searchParameters.get('profile')) ||
    location.search.toLowerCase().includes('profile') ||
    location.pathname.toLowerCase().includes('profile');

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: consumers,
    disableEdit: true,
    disableDelete: true,
    disableExpand: hasProfile,
    onOpenEndConsumerDetails: hasProfile ? null : undefined,
  });

  const column = [
    {
      label: t('CDRId'),
      accessorKey: 'externalId' as keyof ConsumerTransactionHistoryInterFace,
      queryParamsKey: 'cdrObjectId',
      queryParamsValue: '_id',
      sidePanelComponent: <EmspCdrSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('driverId'),
      accessorKey: 'userCustomId' as keyof ConsumerTransactionHistoryInterFace,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      accessorKey: 'sessionId' as any,
      queryParamsKey: 'sessionId',
      queryParamsValue: 'sessionId',
      sidePanelComponent: <EmspSessionSidePanel fetchIdFrom="queryParams" />,
    },

    {
      label: t('locationId'),
      accessorKey: 'locationId' as keyof ConsumerTransactionHistoryInterFace,
      /**
       * Custom rendering of Location ID column.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The rendered location ID.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => {
        const locationId = rowData.location?.id || rowData.locationId || t('na');
        return <span>{locationId}</span>;
      },
    },
    {
      label: t('partyId'),
      accessorKey: 'partyId' as any,
      sortAllow: true,
      /**
       * Render Party ID.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Party ID element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{rowData.partyId || (rowData as any).cdrDetails?.partyId || t('na')}</span>
      ),
    },

    // {
    //   label: t('location', { defaultValue: 'Location' }),
    //   accessorKey: 'location' as any,
    //   /**
    //    * Custom rendering of Location column.
    //    * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
    //    * @returns {React.JSX.Element} The rendered location name.
    //    */
    //   customData: (rowData: ConsumerTransactionHistoryInterFace) => {
    //     const locationName = rowData.location?.name || rowData.chargingPointSn || t('na');
    //     return <span>{locationName}</span>;
    //   },
    // },
    {
      label: t('energy'),
      accessorKey: 'totalEnergy' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total energy in kWh.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total energy element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{ToFixedValue(rowData.totalEnergy, 3)} kWh</span>
      ),
    },
    {
      label: t('CDRamount'),
      accessorKey: 'totalCost' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.totalCost !== undefined && rowData.totalCost !== null
            ? `${Number(rowData.totalCost).toFixed(2)} ${rowData.currency || 'SAR'}`
            : `0.000 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('platformAmount'),
      accessorKey: 'platformAmount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.platformAmount
            ? `${ToFixedValue(Number(rowData.platformAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('sessionAmount'),
      accessorKey: 'sessionAmount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render session amount with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The session amount element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.sessionAmount
            ? `${ToFixedValue(Number(rowData.sessionAmount))} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },
    {
      label: t('finalAmount'),
      accessorKey: 'finalAmount' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render total cost with currency.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total cost element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>
          {rowData.finalAmount !== undefined && rowData.finalAmount !== null
            ? `${ToFixedValue(rowData.finalAmount)} ${rowData.currency || 'SAR'}`
            : `0.00 ${rowData.currency || 'SAR'}`}
        </span>
      ),
    },

    {
      label: t('duration', { defaultValue: 'Time' }),
      accessorKey: 'totalTime' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: true,
      /**
       * Render duration using FormatDuration helper.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The total duration element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => {
        return <span>{FormatDuration(rowData.totalTime)}</span>;
      },
    },
    {
      label: t('invoiceId'),
      accessorKey: 'invoiceId' as any,
      sortAllow: true,
      /**
       * Render Invoice ID.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Invoice ID element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{rowData?.invoiceId || (rowData as any).invoiceNumber || t('na')}</span>
      ),
    },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'paymentStatus' as keyof ConsumerTransactionHistoryInterFace,
      sortAllow: false,
      /**
       * Render Payment Status badge.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The Payment Status badge element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => {
        const rawStatus =
          rowData.paymentStatus ||
          rowData.billingStatus ||
          (rowData.isBilled || rowData.isPaid ? 'SUCCESS' : 'PENDING');

        const upperStatus = String(rawStatus || '').toUpperCase();

        let statusClass = 'pending';
        let statusLabel = upperStatus;

        if (upperStatus === 'SUCCESS' || upperStatus === 'PAID') {
          statusClass = 'success';
          statusLabel = t('paid', { defaultValue: 'Paid' });
        } else if (upperStatus === 'FAILED' || upperStatus === 'UNPAID') {
          statusClass = 'failed';
          statusLabel = t('failed', { defaultValue: 'Failed' });
        } else if (upperStatus === 'NOT_APPLICABLE' || upperStatus === 'NOT APPLICABLE') {
          statusClass = 'notApplicable';
          statusLabel = t('notApplicable', { defaultValue: 'Not Applicable' });
        } else {
          statusClass = 'pending';
          statusLabel = t('pending', { defaultValue: 'Pending' });
        }

        return (
          <span className={`billing-status-badge ${statusClass}`}>
            <span className="dot" />
            {statusLabel}
          </span>
        );
      },
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof ConsumerTransactionHistoryInterFace,
      /**
       * Render creation timestamp in formatted string.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The creation timestamp element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{ConvertUTCtoDateTime(rowData.createdAt)}</span>
      ),
    },
    {
      label: t('Last Updated'),
      accessorKey: 'updatedAt' as keyof ConsumerTransactionHistoryInterFace,
      /**
       * Render updated timestamp in formatted string.
       * @param {ConsumerTransactionHistoryInterFace} rowData - The row data.
       * @returns {React.JSX.Element} The updated timestamp element.
       */
      customData: (rowData: ConsumerTransactionHistoryInterFace) => (
        <span>{ConvertUTCtoDateTime(rowData.updatedAt || '')}</span>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('CDRId'), accessorKey: 'externalId' },
    { label: t('driverId'), accessorKey: 'userName' },
    { label: t('sessionId', { defaultValue: 'Session ID' }), accessorKey: 'sessionId' },
    { label: t('locationId'), accessorKey: 'locationId' },
    { label: t('partyId', { defaultValue: 'Party ID' }), accessorKey: 'partyId' },
    { label: t('energy'), accessorKey: 'totalEnergy' },
    { label: t('CDRamount'), accessorKey: 'totalCost' },
    { label: t('platformAmount'), accessorKey: 'platformAmount' },
    { label: t('sessionAmount'), accessorKey: 'sessionAmount' },
    { label: t('finalAmount'), accessorKey: 'finalAmount' },
    { label: t('duration', { defaultValue: 'Time' }), accessorKey: 'totalTime' },
    { label: t('invoiceId', { defaultValue: 'Invoice ID' }), accessorKey: 'invoiceId' },
    {
      label: t('paymentStatus', { defaultValue: 'Payment Status' }),
      accessorKey: 'paymentStatus',
    },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {string} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    if (!selectedEndConsumer?.userCustomId) return;

    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;
    const cleanedPayload = removeEmptyKeys(payload)

    endConsumerApi
      .fetchCommercialTransactionExport({
        userCustomId: selectedEndConsumer.userCustomId,
        ...cleanedPayload,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerTransactionHistoryInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'externalId':
                return row.chargingTransactionId || row.externalId || t('na');

              case 'userName':
                return row.userCustomId || row.userName || t('na');

              case 'locationId':
                return row.location?.id || row.locationId || t('na');

              case 'totalEnergy':
                return row.totalEnergy !== undefined && row.totalEnergy !== null
                  ? `${ToFixedValue(row.totalEnergy, 3)} kWh`
                  : t('na');

              case 'totalCost':
                return row.totalCost !== undefined && row.totalCost !== null
                  ? `${ToFixedValue(row.totalCost)} ${row.currency || 'SAR'}`
                  : t('na');

              case 'totalTime': {
                return FormatDuration(row.totalTime);
              }

              case 'platform':
              case 'platformAmount':
                return row.platformAmount !== undefined && row.platformAmount !== null
                  ? `${ToFixedValue(row.platformAmount)} ${row.currency || 'SAR'}`
                  : `0.00 ${row.currency || 'SAR'}`;

              case 'sessionAmount':
                return row.sessionAmount !== undefined && row.sessionAmount !== null
                  ? `${ToFixedValue(row.sessionAmount)} ${row.currency || 'SAR'}`
                  : `0.00 ${row.currency || 'SAR'}`;

              case 'finalAmount':
                return row.finalAmount !== undefined && row.finalAmount !== null
                  ? `${ToFixedValue(row.finalAmount)} ${row.currency || 'SAR'}`
                  : `0.00 ${row.currency || 'SAR'}`;

              case 'sessionId':
                return row.sessionId || (row as any).cdrDetails?.sessionId || t('na');

              case 'partyId':
                return row.partyId || (row as any).cdrDetails?.partyId || t('na');

              case 'billingStatus':
              case 'paymentStatus':
                return (
                  row.paymentStatus ||
                  row.billingStatus ||
                  (row as any).payment?.billingStatus ||
                  t('na')
                );

              case 'invoiceId':
              case 'invoiceNumber':
                return row?.invoiceId || (row as any).invoiceNumber || t('na');

              case 'createdAt':
                return row.createdAt
                  ? `${ConvertUTCtoDate(row.createdAt)} ${convertUtcTo12HourTime(row.createdAt)}`
                  : t('na');

              case 'updatedAt':
                return row.updatedAt
                  ? `${ConvertUTCtoDate(row.updatedAt)} ${convertUtcTo12HourTime(row.updatedAt)}`
                  : t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-transaction-history.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-transaction-history.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - Pagination limit value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ 
      ...queryParameters, 
      ...value, 
      sortField, 
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const previousDateRange = React.useRef({ startDate: '', endDate: '' });

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    if (
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate
    ) {
      previousDateRange.current = dateRange;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  }, [dateRange.startDate, dateRange.endDate, filters, limit, selectedEndConsumer]);

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('TransactionHistoryV2');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });
      }
    }
    sessionStorage.setItem('TransactionHistoryV2', currentId);
  }, [filters, selectedEndConsumer, isFilterUpdated, consumers.length, dateRange.startDate, dateRange.endDate, limit]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <CustomTable
        dataKey="EndConsumerManagement"
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerTransactionHistoryListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerTransactionHistoryFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleRefresh}
        refreshInterval={10}
        hoverLabel={t('refresh')}
        rightTopContent={
          <div className="table-bottom-button-list">
            {renderDateFilters()}
          </div>
        }
        onClearFilterChange={() => {
          clearDateRange();
        }}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="TransactionHistoryV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={handleSidePanelClose}
      />
    </div>
  );
}

export default TransactionHistoryList;
